import Table, { ComputeRule, EditedRow } from './components/Table';
import Toolbar from './components/Toolbar';
import { DynamicTableFilter, LoadingType } from '../../types/common';
import useDynamicTable from './hooks/useDynamicTable';
import useTableSchemaLoader from './hooks/useTableSchemaLoader';
import { useEffect } from 'react';
import { useQueryClient } from 'react-query';
import { useApiInjection } from './hooks/useApiInjection';
import TableImportBlock from './components/ImportBlock';
import TableInvitationBlock from './components/InvitationBlock';

export interface APIInjection {
  name: string;
  url: string;
  method: string;
  injectDataToField: string;
  mapper: {
    label: string;
    value: string;
  };
}
export interface DynamicTableProps {
  id?: string;
  schema: {
    columns: any[];
    subRowColumns?: any[];
    options?: {
      showToolbar?: boolean;
      showImportBlock?: boolean;
      showPagination?: boolean;
      showFilter?: boolean;
      filterColumn?: number;
      filters?: DynamicTableFilter[];
      groupBy?: string[];
      showSearch?: boolean;
      showSelectedFilter?: boolean;
      paginationCount?: number;
    };
    computeRules?: ComputeRule[];
    apiInjection?: APIInjection[];
    subRowApiInjection?: APIInjection[];
  };
  handlePublishAward?: any;
  handleSendEmail?: any;
  templateDatas?: any;
  inivitationBlockDatas?: InvitationBlockData;
  subRowColumns?: any[];
  data: any;
  isTableFor?: string;
  loading?: LoadingType;
  totalCounts?: number;
  showPagination?: boolean;
  showPaginationList?: boolean;
  showRow?: boolean;
  pageOfIndex?: number;
  containerClassName?: string;
  checkedValues?: string[];
  showCheckBox?: boolean;
  onChecked?: any;
  border?: string;
  padding?: number;
  checkboxFor?: string;
  coloredTable?: boolean;
  showColumn?: boolean;
  showEarnMultiplier?: boolean;
  showBulkExport?: boolean;
  showSendInvite?: boolean;
  showSendEmail?: boolean;
  showPublishAward?: boolean;
  hasHeader?: boolean;
  scrollableHeight?: number;
  tableColumnList?: any;
  selectedColumnList?: string[];
  handleEarnMultiplier?: (columnValue: string) => void;
  handleTableColumn?: (columnValue: string) => void;
  handleExportBulk?: (value?: string) => void;
  handleSendInvite?: (value?: string) => void;
  handleColumnSave?: (value: string[]) => void;
  tablePreferenceLoading?: boolean;
  setIsHovering?: any;
  filteredData?: any;
  selectedTableData?: any;
  setSelectedTableData?: any;
  onSave?: (editedData: EditedRow[]) => Promise<void>;
  isSaveLoading?: boolean;
  groupBy?: string[];
  subRowKey?: string;
  computeRules?: ComputeRule[];
  selectExport?: any;
  checkedItems?: any;
  setCheckedItems?: any;
  setInviteStatus?: any;
  filteredKey?: string;
  filter?: any;
  filterControls?: Record<string, Record<string, any>>;
  components?: Record<string, () => JSX.Element>;
}
interface InvitationBlockData {
  buttons: Array<{
    variant: 'primary' | 'secondary-outline';
    title: string;
    handleAction: any;
    icon?: string;
    fontColor?: string;
    borderColor?: string;
  }>;
  heading?: {
    title: string;
    showHeading: boolean;
  };
}

function DynamicTable(props: DynamicTableProps) {
  const {
    id,
    schema: { columns, subRowColumns, options, apiInjection, subRowApiInjection, computeRules },
    data,
    templateDatas,
    inivitationBlockDatas,
    // selectExport,
    totalCounts,
    loading,
    filter,
    filterControls,
    selectedColumnList,
    tablePreferenceLoading,
    filteredData,
    tableColumnList,
    components,
    onSave,
    handleColumnSave,
    isSaveLoading = false,
    showPublishAward,
    showSendEmail,
    isTableFor,
    onChecked,
    checkedValues,
    showCheckBox,
    showBulkExport,
    showSendInvite,
    showColumn,
    checkboxFor,
    showEarnMultiplier,
    handleExportBulk,
    handleEarnMultiplier,
    handleSendInvite,
    checkedItems,
    setCheckedItems,
    handlePublishAward,
    handleSendEmail,
    setInviteStatus,
    filteredKey,
    selectedTableData,
    setSelectedTableData,
  } = props;
  const showToolbar = options?.showToolbar || false;
  const showImportBlock = options?.showImportBlock || false;
  const { tableInfo, setTableInfo, searchQuery, setSearchQuery, isSelectAllActive } =
    useDynamicTable();

  // const { showPublishAward, showSendEmail } = selectExport || {};
  // const { downloadValue, importValue, importName } = importDownloadDatas;
  const queryClient = useQueryClient();
  // console.log("Schema", props.schema);

  useEffect(() => {
    return () => {
      options?.filters?.forEach((filter) => {
        if (filter.type === 'asyncSelect') {
          void queryClient.removeQueries({
            queryKey: ['asyncSelectQuery', filter.name, filter.fetchFrom],
          });
        }
      });
    };
  }, []);

  const { injectedData, status } = useApiInjection(apiInjection);
  const { injectedData: subRowInjectedData } = useApiInjection(subRowApiInjection);
  // console.log('injected data', injectedData);

  const updatedColumns = columns.map((column) => {
    const injectedColumnData = injectedData.find((data) => data.field === column.accessor);
    // console.log('column', column, injectedColumnData);

    if (injectedColumnData) {
      return {
        ...column,
        data: injectedColumnData.data,
      };
    }
    return column;
  });

  const subRowUpdatedColumns = subRowColumns?.map((column) => {
    const injectedColumnData = subRowInjectedData.find((data) => data.field === column.accessor);

    if (injectedColumnData) {
      return {
        ...column,
        data: injectedColumnData.data,
      };
    }
    return column;
  });

  return (
    <div id={id}>
      {showToolbar && (
        <Toolbar
          searchQuery={searchQuery}
          setSearchQuery={setSearchQuery}
          totalCounts={options?.paginationCount}
          tableInfo={tableInfo}
          setTableInfo={setTableInfo}
          showFilter={options?.showFilter}
          filters={options?.filters}
          filterControls={filterControls}
          components={components}
          filterColumn={options?.filterColumn}
          showSearch={options?.showSearch}
          showSelectedFilter={options?.showSelectedFilter}
        />
      )}
      {showImportBlock && (
        <TableImportBlock
          dropdownEnable={false}
          templateDatas={templateDatas}
        // importName={importName}
        // downloadValue={downloadValue}
        // importValue={importValue}
        />
      )}
      {/* {(showEarnMultiplier || showPublishAward || showSendEmail) && ( */}
      {inivitationBlockDatas && (
        <TableInvitationBlock
          // selectExport={selectExport}
          inivitationBlockDatas={inivitationBlockDatas}
          checkedValues={checkedValues!}
          showEarnMultiplier={showEarnMultiplier}
          showPublishAward={showPublishAward}
          showSendEmail={showSendEmail}
          handlePublishAward={handlePublishAward}
          handleSendEmail={handleSendEmail}
          handleEarnMultiplier={handleEarnMultiplier}
          handleBulkExport={handleExportBulk}
          filter={filter}
          totalCounts={totalCounts}
          isSelectAllActive={isSelectAllActive}
        />
      )}
      <Table
        {...options}
        // columns={columns}
        columns={updatedColumns}
        subRowColumns={subRowUpdatedColumns}
        data={data}
        loading={status === 'loading' ? status : loading}
        totalCounts={totalCounts ?? 10}
        tableInfo={tableInfo}
        setTableInfo={setTableInfo}
        onSave={onSave}
        isSaveLoading={isSaveLoading}
        groupBy={options?.groupBy}
        computeRules={computeRules}
        // selectExport={selectExport}
        selectedColumnList={selectedColumnList}
        tableColumnList={tableColumnList}
        filteredData={filteredData}
        tablePreferenceLoading={tablePreferenceLoading}
        handleColumnSave={handleColumnSave}
        onChecked={onChecked}
        checkedValues={checkedValues}
        showCheckBox={showCheckBox}
        showBulkExport={showBulkExport}
        showSendInvite={showSendInvite}
        checkboxFor={checkboxFor}
        showColumn={showColumn}
        handleExportBulk={handleExportBulk}
        handleSendInvite={handleSendInvite}
        isTableFor={isTableFor}
        checkedItems={checkedItems}
        setCheckedItems={setCheckedItems}
        setInviteStatus={setInviteStatus}
        filteredKey={filteredKey}
        selectedTableData={selectedTableData}
        setSelectedTableData={setSelectedTableData}
      />
    </div>
  );
}

export { useTableSchemaLoader };
export default DynamicTable;

// --- DynamicTableV2 exports ---
export { DynamicTableV2 } from './DynamicTableV2';
export type { DynamicTableV2Props, DynamicTableV2Handle } from './DynamicTableV2';
export type {
  ExternalTableSchema,
  ExternalColumnDef,
  InternalTableSchema,
  InternalColumnDef,
  ColumnType,
  DataType,
  ActionDef,
  FilterDef,
  EditableDef,
  ContinuationCursorConfig,
  ContinuationCursorFormat,
  LoadMoreCursorConfig,
  ActionDialogConfig,
  ActionDialogKind,
  BackgroundJobConfig,
  OnSuccessAction,
  SectionDef,
  SharedControlsDef,
  InternalMultiSectionSchema,
  InternalSection,
  InternalSharedControls,
  ToolbarGroupDef,
  InternalToolbarGroup,
  ActionIntentConfig,
  ConditionalDisplayRule,
  ConditionalDisplayConfig,
  CellStyle,
  ShowAs,
  RowConditionOperator,
  RowCondition,
  RowStyleRule,
} from './core/schema/types';

export type { BackgroundJobResult } from './hooks/useActionExecutor';

export {
  registerItemsTransform,
  applyItemsTransform,
} from './core/itemsTransformRegistry';
export { normalizeSchema } from './core/schema/normalizer';
export { normalizeMultiSectionSchema } from './core/schema/multiSectionNormalizer';
export { validateMultiSectionSchema } from './core/schema/multiSectionValidator';
export { isMultiSectionSchema } from './core/schema/version';
export { PluginRegistry } from './core/plugins/PluginRegistry';
export { PluginRegistryMap } from './core/plugins/PluginRegistryMap';
export { createDefaultRegistry } from './core/plugins/built-in';
export { useTableState } from './core/state/useTableState';
export { useDataInjectionV2 } from './hooks/useDataInjectionV2';
export { useComputeRulesV2 } from './hooks/useComputeRulesV2';
export { useRemoteDataV2 } from './hooks/useRemoteDataV2';
export { useSchemaV2 } from './hooks/useSchemaV2';
export { useActionExecutor } from './hooks/useActionExecutor';

// --- TanStack Table v8 adapter ---
export { buildColumnDefs, buildFeatureOptions } from './core/adapter/tanstack-adapter';
export type { WealthlaneColumnDef, WealthlaneColumnMeta, TanStackFeatureOptions } from './core/adapter/tanstack-adapter';
export { useTableInstance } from './core/adapter/useTableInstance';
export type { UseTableInstanceConfig } from './core/adapter/useTableInstance';

// --- V1 → V2 legacy bridge ---
export { useLegacyBridge, checkBridgeCompatibility } from './core/schema/legacy-bridge';
export type { LegacyBridgeResult } from './core/schema/legacy-bridge';
export { adaptLegacySchema, detectSchemaVersion, mapLegacyPropsToV2Callbacks } from './core/schema/legacy-adapter';
export { mergeServerTableSchema } from './core/schema/mergeServerTableSchema';

// --- Decomposed engine components ---
export { TableShell } from './core/engine/TableShell';
export type { TableShellProps } from './core/engine/TableShell';
export { PaginationRenderer } from './core/engine/PaginationRenderer';
export type { PaginationRendererProps } from './core/engine/PaginationRenderer';

// --- Action Registry ---
export { ActionRegistry } from './registry/ActionRegistry';
export type {
  ActionExecutionContext,
  ActionableResult,
  FormMode,
  PermissionResolver,
} from './registry/ActionRegistry';
export { AbstractAction, action } from './registry/Action';
export type { Action, ActionIntent, ActionResult, ModalIntentProps, ComposeModalConfig, ComposeModalAction, ComposeModalMode, ComposePopoverConfig } from './registry/Action';
export { evaluateIsActionable } from './utils/evaluateIsActionable';
export { refreshTable } from './utils/refreshTable';
export {
  refreshEntityData,
  matchesEntityRefresh,
  ENTITY_REFRESH_EVENT,
  /** @deprecated Use invalidateActionCache from @packages/cache-manager */
  invalidateActions,
} from './utils/invalidateActions';
export type { EntityRefreshDetail } from './utils/invalidateActions';
export { invalidateActionCache } from '@packages/cache-manager';
export {
  computeHasSelectedItems,
  resolveSelectedRows,
  type TableSelectionMeta,
} from './utils/selectionHelpers';
export {
  IMPORT_EXPORT_SEMANTIC_IDS,
  resolveImportExportSemanticId,
} from './utils/importExportSemanticIds';

// import Table, { ComputeRule, EditedRow } from "./components/Table";
// import Toolbar from "./components/Toolbar";
// import { DynamicTableFilter, LoadingType } from "@wealthlane/ui/types/common";
// import useDynamicTable from "./hooks/useDynamicTable";
// import { JSX, useEffect } from "react";
// import { useQueryClient } from "react-query";
// import { useApiInjection } from "./hooks/useApiInjection";
// import React from "react";
// import TableImportBlock from "./components/ImportBlock";

// export interface APIInjection {
//   name: string;
//   url: string;
//   method: string;
//   injectDataToField: string;
//   mapper: {
//     label: string;
//     value: string;
//   };
// }

// type DynamicTableProps = {
//   schema: {
//     columns: any[];
//     subRowColumns?: any[];
//     options?: {
//       showToolbar?: boolean;
//       showPagination?: boolean;
//       showFilter?: boolean;
//       filterColumn?: number;
//       filters?: DynamicTableFilter[];
//       groupBy?: string[];
//       showSearch?: boolean;
//     };
//     computeRules?: ComputeRule[];
//     apiInjection?: APIInjection[];
//     subRowApiInjection?: APIInjection[];
//   };
//   data?: any;
//   totalCounts?: number;
//   templateDatas?: any;
//   loading?: LoadingType;
//   filterControls?: Record<string, Record<string, any>>;
//   components?: Record<string, () => JSX.Element>;
//   onSave?: (editedData: EditedRow[]) => Promise<void>;
//   isSaveLoading?: boolean;
// };

// function DynamicTable(props: DynamicTableProps) {
//   const {
//     schema: {
//       columns,
//       subRowColumns,
//       options,
//       apiInjection,
//       subRowApiInjection,
//       computeRules,
//     },
//     data,
//     templateDatas,
//     totalCounts,
//     loading,
//     filterControls,
//     components,
//     onSave,
//     isSaveLoading = false,
//   } = props;
//   const showToolbar = options?.showToolbar || false;
//   const {
//     tableInfo,
//     setTableInfo,
//     searchQuery,
//     setSearchQuery,
//     checkedValues,
//     setCheckedValues,
//   } = useDynamicTable();
//   const queryClient = useQueryClient();

//   useEffect(() => {
//     return () => {
//       options?.filters?.forEach((filter) => {
//         if (filter.type === "asyncSelect") {
//           void queryClient.removeQueries({
//             queryKey: ["asyncSelectQuery", filter.name, filter.fetchFrom],
//           });
//         }
//       });
//     };
//   }, []);

//   const { injectedData, status } = useApiInjection(apiInjection);
//   const { injectedData: subRowInjectedData } =
//     useApiInjection(subRowApiInjection);

//   const updatedColumns = columns.map((column) => {
//     const injectedColumnData = injectedData.find(
//       (data) => data.field === column.accessor
//     );

//     if (injectedColumnData) {
//       return {
//         ...column,
//         data: injectedColumnData.data,
//       };
//     }
//     return column;
//   });

//   const subRowUpdatedColumns = subRowColumns?.map((column) => {
//     const injectedColumnData = subRowInjectedData.find(
//       (data) => data.field === column.accessor
//     );

//     if (injectedColumnData) {
//       return {
//         ...column,
//         data: injectedColumnData.data,
//       };
//     }
//     return column;
//   });

//   return (
//     <div>
//       {showToolbar && (
//         <Toolbar
//           searchQuery={searchQuery}
//           setSearchQuery={setSearchQuery}
//           tableInfo={tableInfo}
//           setTableInfo={setTableInfo}
//           showFilter={options?.showFilter}
//           filters={options?.filters}
//           filterControls={filterControls}
//           components={components}
//           filterColumn={options?.filterColumn}
//           showSearch={options?.showSearch}
//         />
//       )}
//       <TableImportBlock
//         dropdownEnable={false}
//         templateDatas={templateDatas}
//       />
//       <Table
//         {...options}
//         // columns={columns}
//         columns={updatedColumns}
//         subRowColumns={subRowUpdatedColumns}
//         data={data}
//         loading={status === "loading" ? status : loading}
//         totalCounts={totalCounts ?? 10}
//         tableInfo={tableInfo}
//         setTableInfo={setTableInfo}
//         onSave={onSave}
//         isSaveLoading={isSaveLoading}
//         groupBy={options?.groupBy}
//         computeRules={computeRules}
//       />
//     </div>
//   );
// }

// export default DynamicTable;
